import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { MapPin, Calendar, Edit, Activity } from "lucide-react";
import { useWorkouts, Workout } from "@/hooks/useWorkouts";
import { useNavigate } from "react-router-dom";
import { useState } from "react";
import { EditWorkoutModal } from "@/components/EditWorkoutModal";
import { RunHistoryDetailModal } from "@/components/RunHistoryDetailModal";
import { ArnoldWorkoutImportModal } from "@/components/ArnoldWorkoutImportModal";
import { WorkoutProgramImportModal } from "@/components/WorkoutProgramImportModal";
import { WorkoutProgramsSection } from "@/components/WorkoutProgramsSection";
import { LogCustomWorkoutModal } from "@/components/LogCustomWorkoutModal";
import { ActivityPickerDropdown, type ActivityItem } from "@/components/ActivityPickerDropdown";
import { isGpsCardioWorkoutType, findGpsCardioExercise } from "@/lib/gpsCardioWorkout";

import { convertToWorkoutLoggerFormat, parseWorkoutPlanFromMessage } from "@/utils/workoutImporter";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";

export const Workouts = () => {
  const { getRecentWorkouts } = useWorkouts();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [selectedWorkout, setSelectedWorkout] = useState<Workout | null>(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showRunDetailModal, setShowRunDetailModal] = useState(false);
  const [showArnoldModal, setShowArnoldModal] = useState(false);
  const [showProgramImportModal, setShowProgramImportModal] = useState(false);
  const [showCustomWorkoutModal, setShowCustomWorkoutModal] = useState(false);
  const [customActivityName, setCustomActivityName] = useState('');
  const [showAllWorkouts, setShowAllWorkouts] = useState(false);
  
  const allRecentWorkouts = getRecentWorkouts(50);
  const displayedWorkouts = showAllWorkouts ? allRecentWorkouts : allRecentWorkouts.slice(0, 3);

  const handleEditWorkout = (workout: Workout) => {
    const isGpsCardio = isGpsCardioWorkoutType(workout.workout_type);
    setSelectedWorkout(workout);
    if (isGpsCardio) {
      setShowRunDetailModal(true);
    } else {
      setShowEditModal(true);
    }
  };

  const handleCloseEditModal = () => {
    setShowEditModal(false);
    setShowRunDetailModal(false);
    setSelectedWorkout(null);
  };

  const handleArnoldWorkoutImport = async (arnoldWorkout: any) => {
    try {
      console.log('Importing Arnold workout:', arnoldWorkout); // Debug log
      
      // Try to parse JSON workout plan exactly like Chat does
      let exercises: any[] | null = null;
      const parsedPlan = parseWorkoutPlanFromMessage(arnoldWorkout.content || '');

      if (parsedPlan && parsedPlan.type === 'single_workout') {
        exercises = convertToWorkoutLoggerFormat(parsedPlan);
      } else {
        // Fallback: build plan from parsed modal data with same coercion logic as Chat
        const toInt = (val: any) => {
          if (val === null || val === undefined) return undefined;
          if (typeof val === 'number' && Number.isFinite(val)) return Math.round(val);
          if (typeof val === 'string') {
            const match = val.match(/-?\d+(?:\.\d+)?/);
            if (match) return Math.round(parseFloat(match[0].replace(',', '.')));
          }
          return undefined;
        };
        const toFloat = (val: any) => {
          if (val === null || val === undefined) return undefined;
          if (typeof val === 'number' && Number.isFinite(val)) return val;
          if (typeof val === 'string') {
            const match = val.replace(',', '.').match(/-?\d+(?:\.\d+)?/);
            if (match) return parseFloat(match[0]);
          }
          return undefined;
        };
        const fallbackPlan = {
          type: 'single_workout' as const,
          exercises: arnoldWorkout.exercises?.map((ex: any) => ({
            exercise_name: String(ex.name || '').trim(),
            sets: toInt(ex.sets),
            reps: toInt(ex.reps),
            weight_kg: toFloat(ex.weight),
          })).filter((ex: any) => ex.exercise_name && typeof ex.sets === 'number' && typeof ex.reps === 'number') || []
        };
        exercises = convertToWorkoutLoggerFormat(fallbackPlan);
      }

      if (exercises && exercises.length > 0) {
        console.log('Converted exercises for GymWorkout:', exercises);
        localStorage.removeItem('activeWorkoutSession');
        localStorage.setItem('importedWorkout', JSON.stringify({
          title: 'AI Workout Plan',
          exercises,
          imported: true
        }));
        toast.success(t('workouts.importedToast'));
        navigate('/gym-workout');
      }

    } catch (error) {
      console.error('Error importing Arnold workout:', error);
    }
  };

  return (
    <div className="py-4 px-4 sm:px-6 pb-24 space-y-6">
      <div className="text-center space-y-2">
        <h1 className="text-xl sm:text-2xl font-normal">{t('workouts.title')}</h1>
        <p className="text-sm sm:text-base text-muted-foreground">{t('workouts.subtitle')}</p>
      </div>

      {/* Log Workout Dropdown */}
      <div className="flex justify-center mb-6">
        <ActivityPickerDropdown onSelectActivity={(activity) => {
          switch (activity.action) {
            case 'gym':
              navigate('/gym-workout');
              break;
            case 'running':
              navigate('/running-start');
              break;
            case 'import':
              setShowArnoldModal(true);
              break;
            case 'import_program':
              setShowProgramImportModal(true);
              break;
            case 'custom':
              if (activity.id === 'walking' || activity.id === 'hiking') {
                navigate('/running-start', { state: { gpsActivity: activity.id } });
                break;
              }
              setCustomActivityName(activity.id === 'other' ? '' : activity.label);
              setShowCustomWorkoutModal(true);
              break;
          }
        }} />
      </div>
      
      <Separator className="my-8" />

      {/* Workout Programs Section */}
      <WorkoutProgramsSection />

      <Separator className="my-8" />

      {/* Workout History */}
      <Card className="bg-gradient-card border-0">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 font-normal">
            <Calendar className="h-5 w-5 text-primary" />
            {t('workouts.history')}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {allRecentWorkouts.length > 0 ? (
            <div className="space-y-3">
              {displayedWorkouts.map((workout) => {
                // Parse running-specific data from exercise notes
                const isGpsCardio = isGpsCardioWorkoutType(workout.workout_type);
                const runningExercise = isGpsCardio ? findGpsCardioExercise(workout.exercises) : null;
                let runningData: any = null;
                if (runningExercise?.notes) {
                  try { runningData = JSON.parse(runningExercise.notes); } catch {}
                }
                const distanceKm = runningExercise?.distance_km;
                const durationSec = runningExercise?.duration_seconds;
                const pace = runningData?.pace;

                return (
                <div 
                  key={workout.id} 
                  className="flex items-center justify-between p-4 rounded-2xl backdrop-blur-xl bg-white/[0.03] hover:bg-white/[0.05] transition-colors cursor-pointer group shadow-[inset_0_1px_1px_rgba(255,255,255,0.04),0_4px_24px_rgba(0,0,0,0.2)]"
                  onClick={() => handleEditWorkout(workout)}
                >
                  <div className="flex-1">
                    <h4 className="font-semibold">{workout.title}</h4>
                    {isGpsCardio ? (
                      <>
                        <p className="text-sm text-muted-foreground">
                          {distanceKm ? `${distanceKm} km` : ''}{distanceKm && durationSec ? ' • ' : ''}{durationSec ? `${Math.floor(durationSec / 60)}:${String(durationSec % 60).padStart(2, '0')}` : `${workout.duration_minutes}min`}{pace ? ` • ${pace}/km` : ''}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          {new Date(workout.completed_at!).toLocaleDateString()}
                        </p>
                      </>
                    ) : (
                      <>
                        <p className="text-sm text-muted-foreground">
                          {workout.workout_type} • {workout.duration_minutes}min
                        </p>
                        <p className="text-xs text-muted-foreground">
                          {new Date(workout.completed_at!).toLocaleDateString()}
                        </p>
                      </>
                    )}
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="text-right">
                      {isGpsCardio ? (
                        <>
                          <p className="text-sm font-medium text-primary">
                            {distanceKm ? `${distanceKm} km` : t('workouts.running')}
                          </p>
                          {workout.calories_burned ? (
                            <p className="text-xs text-muted-foreground">
                              {workout.calories_burned} {t('workouts.kcal')}
                            </p>
                          ) : null}
                        </>
                      ) : (
                        <>
                          <p className="text-sm font-medium text-primary">
                            {t('workouts.exercisesCount', { count: workout.exercises?.length || 0 })}
                          </p>
                          {workout.calories_burned && (
                            <p className="text-xs text-muted-foreground">
                              {workout.calories_burned} {t('workouts.cal')}
                            </p>
                          )}
                        </>
                      )}
                    </div>
                    <Edit className="h-4 w-4 text-muted-foreground group-hover:text-foreground transition-colors" />
                  </div>
                </div>
                );
              })}
              {allRecentWorkouts.length > 3 && (
                <div className="pt-4 text-center">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setShowAllWorkouts(!showAllWorkouts)}
                    className="w-full"
                  >
                    {showAllWorkouts ? t('workouts.showLess') : t('workouts.showAll', { count: allRecentWorkouts.length })}
                  </Button>
                </div>
              )}
            </div>
          ) : (
            <div className="text-center py-8">
              <p className="text-muted-foreground">{t('workouts.noWorkouts')}</p>
              <p className="text-sm text-muted-foreground">{t('workouts.startLogging')}</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Edit Workout Modal */}
      <EditWorkoutModal 
        isOpen={showEditModal}
        onClose={handleCloseEditModal}
        workout={selectedWorkout}
      />

      {/* Run History Detail Modal */}
      <RunHistoryDetailModal
        workout={selectedWorkout}
        open={showRunDetailModal}
        onClose={handleCloseEditModal}
      />

      <ArnoldWorkoutImportModal 
        isOpen={showArnoldModal}
        onClose={() => setShowArnoldModal(false)}
        onWorkoutImport={handleArnoldWorkoutImport}
      />

      <WorkoutProgramImportModal
        isOpen={showProgramImportModal}
        onClose={() => setShowProgramImportModal(false)}
        onImport={(programDay, programTitle) => {
          setShowProgramImportModal(false);
          // Navigate to gym workout with the program day exercises pre-loaded
          navigate('/gym-workout', { state: { programDay, programTitle } });
        }}
      />

      {/* Log Custom Workout Modal */}
      <LogCustomWorkoutModal 
        isOpen={showCustomWorkoutModal}
        onClose={() => setShowCustomWorkoutModal(false)}
        defaultActivityName={customActivityName}
        onSuccess={() => {
          window.location.reload();
        }}
      />
    </div>
  );
};